// src/app/useInteractionLayer.js
//
// WOS-INTERACTION-S0: Injects a single managed <style id="wos-interaction-layer">
// block into the document <head> on first mount and keeps it current when the
// component re-renders (idempotent — same id means the browser replaces in-place
// rather than appending a second block).
//
// Contract:
//   - Zero raw literals inside the injected CSS text.
//     Every value (colour, motion duration, easing curve, radius, spacing) is
//     read from the T token object at call time. This keeps the injected block
//     a single-source-of-truth consumer of tokens.js.
//   - The injected block defines custom properties and structural rules ONLY.
//     It never overrides component-level styles; it seeds the vocabulary that
//     later slices will consume.

import { useEffect } from "react";
import { T } from "./tokens.js";

// ─── CSS text builder ─────────────────────────────────────────────────────────
//
// Builds the full CSS text string by reading token values from T at call-time.
// Kept as a plain function (not inside the hook) so it can be unit-tested
// independently of the React lifecycle.

export function buildInteractionLayerCSS() {
  return `
/* ── WOS-INTERACTION-S0: custom property seed ──────────────────────────── */
:root {

  /* Motion durations — seeded from T.motDuration* tokens.
     Collapse to 0.001ms under prefers-reduced-motion (see media query below). */
  --motion-duration-instant:  ${T.motDurationInstant};
  --motion-duration-fast:     ${T.motDurationFast};
  --motion-duration-normal:   ${T.motDurationNormal};
  --motion-duration-slow:     ${T.motDurationSlow};
  --motion-duration-enter:    ${T.motDurationEnter};
  --motion-duration-exit:     ${T.motDurationExit};

  /* Easing curves — seeded from T.ease* tokens */
  --ease-standard:    ${T.easeStandard};
  --ease-decelerate:  ${T.easeDecelerate};
  --ease-accelerate:  ${T.easeAccelerate};
  --ease-spring:      ${T.easeSpring};

  /* Focus ring — 2 px blue ring; seeded from T.blue */
  --focus-ring: ${T.blue};

  /* Shadow scale — seeded from T shadow tokens */
  --shadow-xs:        ${T.shadowTileXs};
  --shadow-sm:        ${T.shadowTileSm};
  --shadow-card:      ${T.cardShadow};
  --shadow-card-sm:   ${T.shadowCardSm};
  --shadow-card-md:   ${T.shadowCardMd};
  --shadow-hover:     ${T.shadowHover};
  --shadow-dropdown:  ${T.shadowDropdown};
  --shadow-float:     ${T.shadowFloat};
  --shadow-modal:     ${T.shadowModal};
  --shadow-ask:       ${T.shadowAsk};

  /* Tint surfaces — seeded from T tint tokens */
  --tint-bg:          ${T.tint};
  --tint-border:      ${T.tintBorder};
  --tint-blue-soft:   ${T.blueSoft};
  --tint-light-gray:  ${T.lightGray};

  /* Accent rail — header / sidebar navy */
  --accent-rail:      ${T.hdrFrom};

  /* Border radius scale — seeded from T.radius* tokens */
  --radius-xs:    ${T.radiusXs};
  --radius-sm:    ${T.radiusSm};
  --radius-card:  ${T.radiusCard};
  --radius-md:    ${T.radiusMd};
  --radius-lg:    ${T.radiusLg};
  --radius-xl:    ${T.radiusXl};
  --radius-full:  ${T.radiusFull};

  /* Fluid page-shell spacing — seeded from T.spacePageX/Y (clamp-based).
     Wired to <main> padding in App.jsx via CSS custom properties. */
  --space-page-x: ${T.spacePageX};
  --space-page-y: ${T.spacePageY};
}

/* ── Global :focus-visible ring ─────────────────────────────────────────── */
/*    Applies a visible 2 px ring to every element that receives keyboard   */
/*    focus. Browser :focus (mouse-click) is unaffected. NavRail.css        */
/*    overrides this for nav-button (box-shadow ring) and the nav/main      */
/*    landmarks (intentional scroll-container suppression — see NAV-BLUE-   */
/*    BOX-KILL comment in NavRail.css).                                     */
:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 3px;
}

/* ── Reduced motion ─────────────────────────────────────────────────────── */
/*    Collapses all transition and animation durations to 0.001 ms so the   */
/*    OS "Reduce Motion" preference disables every timed effect site-wide.  */
/*    We do NOT use 0 ms — some animation events rely on transitionend       */
/*    firing; 0.001 ms fires the event immediately without a visual delay.  */
@media (prefers-reduced-motion: reduce) {
  :root {
    --motion-duration-instant: 0.001ms;
    --motion-duration-fast:    0.001ms;
    --motion-duration-normal:  0.001ms;
    --motion-duration-slow:    0.001ms;
    --motion-duration-enter:   0.001ms;
    --motion-duration-exit:    0.001ms;
  }
  *,
  *::before,
  *::after {
    animation-duration:        0.001ms !important;
    animation-iteration-count: 1       !important;
    transition-duration:       0.001ms !important;
    scroll-behavior:           auto    !important;
  }
}

/* ── Utility class stubs ────────────────────────────────────────────────── */
/*    Defined with full property lists but neutral/no-op values so they     */
/*    have zero visual effect at S0. Later slices populate the values.      */
/*    Explicit transition properties only — never "transition: all".        */

.wos-btn {
  color: inherit;
  background: transparent;
  box-shadow: none;
  opacity: 1;
  transform: none;
  transition:
    color           var(--motion-duration-fast)   var(--ease-standard),
    background      var(--motion-duration-fast)   var(--ease-standard),
    box-shadow      var(--motion-duration-fast)   var(--ease-standard),
    opacity         var(--motion-duration-fast)   var(--ease-standard),
    transform       var(--motion-duration-fast)   var(--ease-standard);
}

.wos-btn:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-hover);
}

.wos-btn:active {
  transform: translateY(0);
  box-shadow: var(--shadow-xs);
}

.wos-btn:focus-visible {
  outline: none;
}

.wos-card-interactive {
  color: inherit;
  background: inherit;
  box-shadow: var(--shadow-card);
  transform: none;
  transition:
    box-shadow  var(--motion-duration-normal) var(--ease-standard),
    transform   var(--motion-duration-normal) var(--ease-standard);
}

.wos-card-interactive:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-hover);
}

.wos-card-selected {
  color: inherit;
  background: inherit;
  box-shadow: inherit;
  outline: none;
  transition:
    box-shadow      var(--motion-duration-fast) var(--ease-standard),
    background      var(--motion-duration-fast) var(--ease-standard),
    outline-color   var(--motion-duration-fast) var(--ease-standard);
}

.wos-row {
  color: inherit;
  background: transparent;
  opacity: 1;
  transition:
    background  var(--motion-duration-fast) var(--ease-standard),
    opacity     var(--motion-duration-fast) var(--ease-standard);
}

.wos-skeleton {
  color: transparent;
  background: transparent;
  opacity: 0;
  transition:
    opacity var(--motion-duration-normal) var(--ease-standard);
}

.wos-fade-up {
  opacity: 1;
  transform: none;
  transition:
    opacity   var(--motion-duration-enter) var(--ease-decelerate),
    transform var(--motion-duration-enter) var(--ease-decelerate);
}

.wos-menu-in {
  opacity: 1;
  transform: none;
  transition:
    opacity   var(--motion-duration-fast)   var(--ease-decelerate),
    transform var(--motion-duration-fast)   var(--ease-decelerate);
}

.wos-drawer-in {
  opacity: 1;
  transform: none;
  transition:
    opacity   var(--motion-duration-enter) var(--ease-decelerate),
    transform var(--motion-duration-enter) var(--ease-decelerate);
}
`;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useInteractionLayer() {
  useEffect(() => {
    const css = buildInteractionLayerCSS();
    let el = document.getElementById("wos-interaction-layer");
    if (!el) {
      el = document.createElement("style");
      el.id = "wos-interaction-layer";
      document.head.appendChild(el);
    }
    el.textContent = css;
  }, []);
}
